import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LocationsService } from 'src/app/core/services/locations/locations.service';

@Component({
    selector: 'covered-cities',
    templateUrl: './covered-cities.component.html',
    styleUrl: './covered-cities.component.scss',
    standalone: false
})
export class CoveredCitiesComponent implements OnInit {

  regions: any[] = [];
  primaryCities: any[] = [];
  additionalCities: any[] = [];
  savedAdditionalRegions: any[] = [];
  savedAdditionalCities: any[] = [];
  coveredCities: {city: number, is_primary: boolean}[] = [];
  showError: boolean = false;
  @Input() inspectorCoveredCities: {city: number, is_primary: boolean, region_id: number}[] = [];
  @Output() coveredCitiesChange = new EventEmitter<{city: number, is_primary: boolean}[]>();
  @Output() disableSave = new EventEmitter<boolean>();

  locationService = inject(LocationsService);
  route = inject(ActivatedRoute);

  coveredCitiesFormGroup: FormGroup = new FormGroup({
    primary_region: new FormControl(null, Validators.required),
    primary_city: new FormControl(null, Validators.required),
    additional_regions: new FormControl([]),
    additional_cities: new FormControl([]),
  });

  ngOnInit() {
    this.regions = this.route.parent.snapshot.data.regions;
    if (this.inspectorCoveredCities && this.inspectorCoveredCities.length > 0) {
      this.setPrimaryRegionCityValues();
      this.setAdditionalRegionsCititesValues();
    }
  }

  setPrimaryRegionCityValues() {
    const primaryCity = this.inspectorCoveredCities.find(c => c.is_primary);
    if (primaryCity) {
      this.coveredCitiesFormGroup.get('primary_region')?.setValue(primaryCity.region_id);
      this.getCitiesByRegion([primaryCity.region_id], 'primary');
      this.coveredCitiesFormGroup.patchValue({ primary_city: primaryCity.city });
    }
  }

  setAdditionalRegionsCititesValues() {
    const additionalCities = this.inspectorCoveredCities.filter(c => !c.is_primary);
    if (additionalCities.length > 0) {
      const uniqueRegionIds = [...new Set(additionalCities.map(c => c.region_id))] as number[];
      this.savedAdditionalRegions = uniqueRegionIds || [];
      this.getCitiesByRegion(uniqueRegionIds, 'additional', additionalCities.map(c => c.city) as number[]);
      this.coveredCitiesFormGroup.patchValue({
        additional_regions: uniqueRegionIds,
        additional_cities: additionalCities.map(c => c.city)
      });
    }
  }

  onPrimaryRegionSelected(event: any) {
    this.coveredCitiesFormGroup.patchValue({ primary_city: null });
    this.primaryCities = [];
    this.getCitiesByRegion([event?.pk], 'primary');
  }

  onAdditionalRegionChanged(event: any) {
    const newRegions = event || [];
    this.coveredCitiesFormGroup.patchValue({ additional_regions: newRegions });

    if (newRegions.length === 0) {
      this.additionalCities = [];
      this.savedAdditionalCities = [];
      this.coveredCitiesFormGroup.patchValue({ additional_cities: [] });
      return;
    }

    const currentSelectedCities = this.coveredCitiesFormGroup.value.additional_cities || [];

    this.getCitiesByRegion(newRegions, 'additional', currentSelectedCities);
  }


  onAdditionalCityChanged(event: any) {
    const cityIds = event || [];
    this.showError = false;
    if(this.checkForDuplicateCity(this.coveredCitiesFormGroup.value.primary_city, cityIds)) {
      this.showError = true;
      this.disableSave.emit(true);
      return;
    }

    this.disableSave.emit(false);
    this.coveredCitiesFormGroup.patchValue({ additional_cities: cityIds });
    this.savedAdditionalCities = [...cityIds];
    this.updateCoveredCities();
  }

  onPrimaryCitySelected(event: any) {
    const primaryCityId = event ? event.pk : null;
    this.showError = false;
    if(this.checkForDuplicateCity(primaryCityId, this.coveredCitiesFormGroup.value.additional_cities || [])) {
      this.showError = true;
      this.disableSave.emit(true);
      return;
    }
    this.disableSave.emit(false);
    this.coveredCitiesFormGroup.patchValue({ primary_city: primaryCityId });
    this.updateCoveredCities();
  }

  checkForDuplicateCity(cityId: number, additionalCities: number[]): boolean {
    return additionalCities.includes(cityId);
  }

  updateCoveredCities() {
    this.coveredCities = [];

    const primaryCityId = this.coveredCitiesFormGroup.value.primary_city;
    if (primaryCityId) {
      this.coveredCities.push({ city: primaryCityId, is_primary: true });
    }

    const additionalCityIds = this.coveredCitiesFormGroup.value.additional_cities || [];
    additionalCityIds.forEach(cityId => {
      // Check for duplicates before adding
      if (!this.coveredCities.some(c => c.city === cityId)) {
        this.coveredCities.push({ city: cityId, is_primary: false });
      }
    });

    this.coveredCitiesChange.emit(this.coveredCities);
  }

  getCitiesByRegion(
    regionIds: number[],
    type: 'primary' | 'additional',
    preserveCities: number[] = []
  ) {
    if (!regionIds || regionIds.length === 0) return;

    this.locationService.getCoveredCities(regionIds).subscribe(
      (cities) => {
        if (type === 'primary') {
          this.primaryCities = cities;
        } else {
          this.additionalCities = cities;

          const validCityIds = preserveCities.filter((cityId) =>
            cities.some((city) => city.pk === cityId)
          );

          this.savedAdditionalCities = [...validCityIds];

          // Update form without emitting event to avoid infinite loops
          this.coveredCitiesFormGroup.patchValue({
            additional_cities: validCityIds
          }, { emitEvent: false });
        }
      },
      (error) => {
        console.error('Error loading cities:', error);
        if (type === 'primary') {
          this.primaryCities = [];
        } else {
          this.additionalCities = [];
          this.savedAdditionalCities = [];
        }
      }
    );
  }
}
